import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Analytics } from '@/lib/analytics';

export interface WorkoutProgramExercise {
  id: string;
  exercise_name: string;
  muscle_groups: string[];
  sets: number | null;
  reps_min: number | null;
  reps_max: number | null;
  weight_kg: number | null;
  rest_seconds: number | null;
  notes: string | null;
  exercise_order: number;
}

export interface WorkoutProgramDay {
  id: string;
  day_number: number;
  day_name: string | null;
  workout_title: string;
  workout_description: string | null;
  estimated_duration_minutes: number | null;
  difficulty_level: string | null;
  completed_at: string | null;
  completed_workout_id: string | null;
  exercises: WorkoutProgramExercise[];
}

export interface WorkoutProgram {
  id: string;
  title: string;
  description: string | null;
  program_type: string | null;
  duration_weeks: number | null;
  created_from_chat: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  message_id?: string | null;
  conversation_id?: string | null;
  days: WorkoutProgramDay[];
}

export const useWorkoutPrograms = () => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState<WorkoutProgram[]>([]);
  const [loading, setLoading] = useState(false);

  console.log('useWorkoutPrograms - current user:', user?.id);

  const fetchPrograms = async () => {
    if (!user) return;
    
    console.log('fetchPrograms called for user:', user.id);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('workout_programs')
        .select(`
          *,
          workout_program_days (
            *,
            workout_program_exercises (*)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      console.log('Raw programs data:', data);
      console.log('Programs query error:', error);

      if (error) throw error;

      const formattedPrograms: WorkoutProgram[] = (data || []).map(program => ({
        ...program,
        days: (program.workout_program_days || [])
          .sort((a: any, b: any) => a.day_number - b.day_number)
          .map((day: any) => ({
            ...day,
            completed_at: day.completed_at || null,
            completed_workout_id: day.completed_workout_id || null,
            exercises: (day.workout_program_exercises || [])
              .sort((a: any, b: any) => a.exercise_order - b.exercise_order)
          }))
      }));

      console.log('Formatted programs:', formattedPrograms);
      setPrograms(formattedPrograms);
    } catch (error) {
      console.error('Error fetching workout programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (programId: string) => {
    try {
      // Get program title before deletion
      const program = programs.find(p => p.id === programId);
      const programTitle = program?.title || 'Unknown Program';
      
      const { error } = await supabase
        .from('workout_programs')
        .delete()
        .eq('id', programId);

      if (error) throw error;

      Analytics.programDeleted(programId, programTitle);
      setPrograms(prev => prev.filter(p => p.id !== programId));
    } catch (error) {
      console.error('Error deleting workout program:', error);
      throw error;
    }
  };

  const updateProgramStatus = async (programId: string, status: string) => {
    try {
      const program = programs.find(p => p.id === programId);
      const programTitle = program?.title || 'Unknown Program';
      
      const { error } = await supabase
        .from('workout_programs')
        .update({ status })
        .eq('id', programId);

      if (error) throw error;

      // Track program status changes
      if (status === 'active') {
        Analytics.programStarted(programId, programTitle);
      } else if (status === 'completed') {
        Analytics.programCompleted(programId, programTitle);
      }

      setPrograms(prev => prev.map(p => 
        p.id === programId ? { ...p, status } : p
      ));
    } catch (error) {
      console.error('Error updating program status:', error);
      throw error;
    }
  };

  const markDayComplete = async (programId: string, dayId: string, dayData: WorkoutProgramDay) => {
    if (!user) return;
    
    try {
      // Check if this is a rest day
      const isRestDay = dayData.workout_title.toLowerCase().includes('rest') || 
                        dayData.exercises.length === 0;

      if (isRestDay) {
        // For rest days, just mark as completed without creating a workout
        const { error: dayError } = await supabase
          .from('workout_program_days')
          .update({
            completed_at: new Date().toISOString(),
            completed_workout_id: null
          } as any)
          .eq('id', dayId);

        if (dayError) throw dayError;

      // Update local state
      setPrograms(prev => prev.map(p => {
        if (p.id === programId) {
          const updatedDays = p.days.map(d => 
            d.id === dayId 
              ? { ...d, completed_at: new Date().toISOString(), completed_workout_id: null }
              : d
          );
          
          // Track program day completed
          Analytics.programDayCompleted(programId, p.title, dayData.day_number || 0);
          
          return { ...p, days: updatedDays };
        }
        return p;
      }));

      return null;
      }

      // Create a workout record from the program day
      const { data: workout, error: workoutError } = await supabase
        .from('workouts')
        .insert({
          user_id: user.id,
          title: dayData.workout_title,
          workout_type: dayData.exercises.some(e => e.exercise_name.toLowerCase().includes('running')) ? 'running' : 'strength',
          duration_minutes: dayData.estimated_duration_minutes || 45,
          difficulty_level: dayData.difficulty_level || 'medium',
          completed_at: new Date().toISOString(),
          notes: `Completed from program day: ${dayData.day_name || `Day ${dayData.day_number}`}`
        })
        .select()
        .single();

      if (workoutError) throw workoutError;

      // Create exercise records
      const exerciseInserts = dayData.exercises.map(exercise => ({
        workout_id: workout.id,
        name: exercise.exercise_name,
        sets: exercise.sets || 3,
        reps: exercise.reps_max || exercise.reps_min || 10,
        weight_kg: exercise.weight_kg,
        rest_seconds: exercise.rest_seconds,
        muscle_groups: exercise.muscle_groups,
        notes: exercise.notes,
        category: exercise.muscle_groups?.[0] || 'Strength'
      }));

      const { error: exercisesError } = await supabase
        .from('exercises')
        .insert(exerciseInserts);

      if (exercisesError) throw exercisesError;

      // Mark the program day as completed
      const { error: dayError } = await supabase
        .from('workout_program_days')
        .update({
          completed_at: new Date().toISOString(),
          completed_workout_id: workout.id
        } as any)
        .eq('id', dayId);

      if (dayError) throw dayError;

      // Update local state
      setPrograms(prev => prev.map(p => {
        if (p.id === programId) {
          const updatedDays = p.days.map(d => 
            d.id === dayId 
              ? { ...d, completed_at: new Date().toISOString(), completed_workout_id: workout.id }
              : d
          );
          
          // Track program day completed
          Analytics.programDayCompleted(programId, p.title, dayData.day_number || 0);
          
          // Check if program is complete
          const allDaysCompleted = updatedDays.every(d => d.completed_at !== null);
          if (allDaysCompleted && p.status !== 'completed') {
            Analytics.programCompleted(programId, p.title);
          }
          
          return { ...p, days: updatedDays };
        }
        return p;
      }));

      return workout;
    } catch (error) {
      console.error('Error marking day complete:', error);
      throw error;
    }
  };

  useEffect(() => {
    console.log('useWorkoutPrograms useEffect triggered, user:', user?.id);
    if (user) {
      fetchPrograms();
    }
  }, [user]);

  return {
    programs,
    loading,
    refetch: fetchPrograms,
    deleteProgram,
    updateProgramStatus,
    markDayComplete
  };
};